<template>
  <v-container class="px-4 py-8" fluid style="max-width: 1400px;">
    <!-- Header -->
    <div class="mb-8 text-center text-md-left">
      <h1 class="text-h3 font-weight-bold primary--text mb-2">
        <v-icon left size="40">mdi-help-circle</v-icon>
        {{ $t('ayuda') }}
      </h1>
      <p class="text-subtitle-1 grey--text text--darken-1">
        {{ getProfile && getProfile.email ? getProfile.email : $t('no_disponible') }}
      </p>
    </div>

    <!-- User Logged In Section -->
    <div v-if="getProfile && getProfile.email" class="mb-12">
      <!-- Tabs Navigation -->
      <v-card class="elevation-4 rounded-xl mb-6">
        <v-tabs v-model="activeTab" background-color="primary" dark slider-color="white" class="rounded-t-xl">
          <v-tab href="#tab-faq" class="font-weight-medium">
            <v-icon left>mdi-frequently-asked-questions</v-icon>
            {{ $t('preguntas_frecuentes') }}
          </v-tab>
          <v-tab href="#tab-tickets" class="font-weight-medium">
            <v-icon left>mdi-ticket-multiple</v-icon>
            {{ $t('mis_tickets') }}
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <!-- FAQ Tab -->
          <v-tab-item value="tab-faq">
            <v-card-text class="pt-8">
              <!-- Search FAQ -->
              <v-row class="mb-2">
                <v-col cols="12" md="6" offset-md="3">
                  <v-text-field
                    v-model="faqSearch"
                    :label="$t('buscar_preguntas')"
                    prepend-inner-icon="mdi-magnify"
                    outlined rounded dense
                    clearable
                  ></v-text-field>
                </v-col>
              </v-row>

              <!-- FAQ Category Filter -->
              <v-row class="mb-4">
                <v-col cols="12">
                  <v-chip-group
                    v-model="selectedCategory"
                    mandatory
                    show-arrows
                    active-class="primary white--text"
                  >
                    <v-chip
                      v-for="cat in faqCategories"
                      :key="cat.value"
                      :value="cat.value"
                      filter
                    >
                      <v-icon left small>{{ cat.icon }}</v-icon>
                      {{ cat.label }}
                    </v-chip>
                  </v-chip-group>
                </v-col>
              </v-row>

              <!-- FAQ Items -->
              <v-row>
                <v-col cols="12">
                  <div v-if="filteredFaqs.length > 0">
                    <v-expansion-panels v-model="expandedFaqs" multiple>
                      <v-expansion-panel
                        v-for="(faq, index) in filteredFaqs"
                        :key="index"
                        class="mb-3 rounded-lg"
                      >
                        <v-expansion-panel-header class="font-weight-medium">
                          <div>
                            <v-icon left small>mdi-help</v-icon>
                            {{ faq.question }}
                            <v-chip x-small outlined class="ml-2" color="primary">
                              <v-icon left x-small>{{ categoryIcon(faq.category) }}</v-icon>
                              {{ categoryLabel(faq.category) }}
                            </v-chip>
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <p class="text-body2 grey--text text--darken-1">{{ faq.answer }}</p>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </div>
                  <div v-else class="text-center py-8">
                    <v-icon large color="grey" class="mb-2">mdi-folder-open-outline</v-icon>
                    <p class="grey--text">{{ $t('no_preguntas_encontradas') }}</p>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-tab-item>

          <!-- Tickets Tab -->
          <v-tab-item value="tab-tickets">

            <v-card-text class="pt-6">
              <v-btn
                color="primary"
                @click="openCreateDialog()"
                rounded large elevation="2" class="mb-6"
              >
                <v-icon left>mdi-plus</v-icon>
                {{ $t('nuevo_ticket') }}
              </v-btn>

              <v-data-table
                :headers="headers"
                :items="tickets"
                :loading="loadingTickets"
                :footer-props="{
                  itemsPerPageOptions: [5, 10, 25, 50],
                  itemsPerPageText: $t('filas_por_pagina')
                }"
                class="elevation-1 rounded-lg"
              >
                <template v-slot:item.status="{ item }">
                  <v-chip :color="getStatusColor(item.status)" text-color="white" small>
                    {{ item.status }}
                  </v-chip>
                </template>

                <template v-slot:item.created_date="{ item }">
                  {{ formatDate(item.created_date) }}
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-icon small @click.stop="openDetailsDialog(item)" class="mr-2">
                    mdi-eye
                  </v-icon>
                </template>

                <template v-slot:no-data>
                  <div class="text-center py-8">
                    <v-icon large color="grey" class="mb-2">mdi-inbox-multiple</v-icon>
                    <p class="grey--text">{{ $t('no_hay_tickets') }}</p>
                  </div>
                </template>
              </v-data-table>

              <!-- Statistics Cards -->
              <v-row v-if="tickets.length > 0" class="mt-6">
                <v-col cols="12" sm="6" md="3">
                  <v-card class="elevation-4 rounded-xl text-center py-6">
                    <div class="primary pa-2 rounded-lg d-inline-block mb-4">
                      <v-icon large color="white">mdi-ticket</v-icon>
                    </div>
                    <p class="text-h5 font-weight-bold primary--text">{{ tickets.length }}</p>
                    <p class="grey--text text--darken-1 text-caption">{{ $t('total_tickets') }}</p>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-card class="elevation-4 rounded-xl text-center py-6">
                    <div class="primary pa-2 rounded-lg d-inline-block mb-4">
                      <v-icon large color="white">mdi-alert-circle</v-icon>
                    </div>
                    <p class="text-h5 font-weight-bold" style="color: #FF9800;">{{ countByStatus('Nuevo') }}</p>
                    <p class="grey--text text--darken-1 text-caption">{{ $t('nuevos') }}</p>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-card class="elevation-4 rounded-xl text-center py-6">
                    <div class="primary pa-2 rounded-lg d-inline-block mb-4">
                      <v-icon large color="white">mdi-progress-clock</v-icon>
                    </div>
                    <p class="text-h5 font-weight-bold" style="color: #2196F3;">{{ countByStatus('En progreso') }}</p>
                    <p class="grey--text text--darken-1 text-caption">{{ $t('en_progreso') }}</p>
                  </v-card>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <v-card class="elevation-4 rounded-xl text-center py-6">
                    <div class="primary pa-2 rounded-lg d-inline-block mb-4">
                      <v-icon large color="white">mdi-check-circle</v-icon>
                    </div>
                    <p class="text-h5 font-weight-bold" style="color: #4CAF50;">{{ countByStatus('Resuelto') }}</p>
                    <p class="grey--text text--darken-1 text-caption">{{ $t('resueltos') }}</p>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <!-- Create Ticket Dialog -->
      <v-dialog v-model="dialogCreateTicket" persistent scrollable max-width="600px">
        <v-card class="rounded-xl">
          <v-toolbar dark color="primary" flat>
            <v-toolbar-title class="font-weight-medium">
              <v-icon left>mdi-ticket-plus</v-icon>
              {{ $t('crear_ticket') }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="dialogCreateTicket = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="pt-6">
            <v-form ref="formCreateTicket" @submit.prevent="submitCreateTicket()">
              <v-text-field
                v-model="newTicket.title"
                :label="$t('titulo')"
                prepend-inner-icon="mdi-pencil"
                :error-messages="errores.title"
                outlined rounded dense class="mb-4"
                required
              ></v-text-field>

              <v-textarea
                v-model="newTicket.description"
                :label="$t('descripcion')"
                prepend-icon="mdi-text"
                :error-messages="errores.description"
                outlined rounded dense class="mb-4"
                rows="4"
              ></v-textarea>

              <v-select
                v-model="newTicket.area_id"
                :items="areas"
                item-text="name"
                item-value="id"
                :label="$t('area')"
                prepend-inner-icon="mdi-folder-multiple"
                :error-messages="errores.area_id"
                outlined rounded dense class="mb-4"
              ></v-select>

              <v-text-field
                v-model="newTicket.client_name"
                :label="$t('nombre_cliente')"
                prepend-inner-icon="mdi-account"
                :error-messages="errores.client_name"
                outlined rounded dense class="mb-4"
                readonly
              ></v-text-field>

              <v-text-field
                v-model="newTicket.client_email"
                type="email"
                :label="$t('email')"
                prepend-inner-icon="mdi-email"
                :error-messages="errores.client_email"
                outlined rounded dense class="mb-4"
                readonly
              ></v-text-field>

              <v-text-field
                v-model="newTicket.client_phone"
                :label="$t('telefono')"
                prepend-inner-icon="mdi-phone"
                :error-messages="errores.client_phone"
                outlined rounded dense class="mb-6"
                readonly
              ></v-text-field>

              <v-alert v-if="errorAlert" type="error" outlined text class="mb-4">
                {{ errorAlert }}
              </v-alert>
            </v-form>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <v-btn color="grey" @click="dialogCreateTicket = false" rounded>
              {{ $t('cancelar') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              @click="submitCreateTicket()"
              :loading="loadingCreateTicket"
              rounded large elevation="2"
            >
              <v-icon left>mdi-plus</v-icon>
              {{ $t('crear') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Ticket Details Dialog -->
      <v-dialog v-model="dialogTicketDetails" persistent scrollable max-width="600px">
        <v-card v-if="selectedTicket" class="rounded-xl">
          <v-toolbar dark color="primary" flat>
            <v-toolbar-title class="font-weight-medium">
              <v-icon left>mdi-information</v-icon>
              {{ $t('detalles_ticket') }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="dialogTicketDetails = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-toolbar>
          <v-card-text class="pt-6">
            <v-row>
              <v-col cols="12">
                <div class="mb-4">
                  <small class="grey--text text--darken-2">{{ $t('id') }}</small>
                  <p class="text-h6 font-weight-bold">#{{ selectedTicket.id }}</p>
                </div>
                <v-divider class="my-4"></v-divider>
              </v-col>

              <v-col cols="12">
                <small class="grey--text text--darken-2">{{ $t('titulo') }}</small>
                <p class="text-body1">{{ selectedTicket.title }}</p>
              </v-col>

              <v-col cols="12">
                <small class="grey--text text--darken-2">{{ $t('descripcion') }}</small>
                <p class="text-body1">{{ selectedTicket.description || '—' }}</p>
              </v-col>

              <v-col cols="12" sm="6">
                <small class="grey--text text--darken-2">{{ $t('estado') }}</small>
                <v-chip :color="getStatusColor(selectedTicket.status)" text-color="white" small class="mt-2">
                  {{ selectedTicket.status }}
                </v-chip>
              </v-col>

              <v-col cols="12" sm="6">
                <small class="grey--text text--darken-2">{{ $t('area') }}</small>
                <p class="text-body1 mt-2">{{ selectedTicket.area }}</p>
              </v-col>

              <v-col cols="12">
                <small class="grey--text text--darken-2">{{ $t('cliente') }}</small>
                <p class="text-body1">{{ selectedTicket.client_name }}</p>
              </v-col>

              <v-col cols="12" sm="6">
                <small class="grey--text text--darken-2">{{ $t('email') }}</small>
                <p class="text-body1">{{ selectedTicket.client_email || '—' }}</p>
              </v-col>

              <v-col cols="12" sm="6">
                <small class="grey--text text--darken-2">{{ $t('fecha_creacion') }}</small>
                <p class="text-body1">{{ formatDate(selectedTicket.created_date) }}</p>
              </v-col>

              <v-col cols="12" sm="6" v-if="selectedTicket.client_phone">
                <small class="grey--text text--darken-2">{{ $t('telefono') }}</small>
                <p class="text-body1">{{ selectedTicket.client_phone }}</p>
              </v-col>

              <v-col cols="12" v-if="selectedTicket.tags && selectedTicket.tags.length > 0">
                <small class="grey--text text--darken-2">{{ $t('etiquetas') }}</small>
                <div class="mt-2">
                  <v-chip
                    v-for="tag in selectedTicket.tags"
                    :key="tag.id"
                    small
                    color="info"
                    text-color="white"
                    class="mr-2"
                  >
                    {{ tag.name }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="dialogTicketDetails = false" rounded>
              {{ $t('cerrar') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </div>

    <!-- Not Logged In Message -->
    <div v-else class="text-center py-12">
      <v-icon size="80" color="grey" class="mb-4">mdi-alert-outline</v-icon>
      <h2 class="text-h5 grey--text text--darken-1 mb-2">{{ $t('no_disponible') }}</h2>
      <p class="text-body2 grey--text">{{ $t('debes_iniciar_sesion') }}</p>
    </div>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { ticketsService } from '@/services/ticketsService'

export default {
  name: 'Ayuda',
  data () {
    return {
      activeTab: 'tab-faq',
      faqSearch: '',
      selectedCategory: 'todas',
      expandedFaqs: [],
      faqCategories: [
        { value: 'todas', label: 'Todas', icon: 'mdi-view-grid-outline' },
        { value: 'cuenta', label: 'Cuenta y Plataforma', icon: 'mdi-account-circle-outline' },
        { value: 'tickets', label: 'Tickets de Soporte', icon: 'mdi-ticket-multiple' },
        { value: 'visitas', label: 'Visitas e Invitados', icon: 'mdi-account-group' },
        { value: 'paqueteria', label: 'Paquetería', icon: 'mdi-package-variant-closed' },
        { value: 'agua', label: 'Servicio de Agua', icon: 'mdi-water' },
        { value: 'cuentas', label: 'Estados de Cuenta', icon: 'mdi-cash-multiple' },
        { value: 'mascotas', label: 'Mascotas', icon: 'mdi-paw' },
        { value: 'casa_club', label: 'Casa Club', icon: 'mdi-pool' },
        { value: 'multas', label: 'Multas de Tránsito', icon: 'mdi-speedometer' },
        { value: 'almacenamiento', label: 'Almacenamiento', icon: 'mdi-database' },
        { value: 'avisos', label: 'Avisos y Directorio', icon: 'mdi-bulletin-board' }
      ],
      faqs: [
        // Cuenta y Plataforma
        {
          category: 'cuenta',
          question: '¿Cómo restablezco mi contraseña?',
          answer: 'Desde la pantalla de inicio de sesión, selecciona "¿Olvidaste tu contraseña?" e ingresa tu correo electrónico registrado. Recibirás un enlace para crear una nueva contraseña.'
        },
        {
          category: 'cuenta',
          question: '¿Qué es la verificación en dos pasos (2FA)?',
          answer: 'Es una capa adicional de seguridad que solicita un código cada vez que inicias sesión. Puedes activarla o desactivarla desde la sección de Configuración.'
        },
        {
          category: 'cuenta',
          question: '¿Cómo actualizo mis datos personales o fotografías de identificación?',
          answer: 'Ingresa a Configuración > Información Personal para editar tu nombre, teléfono, fotografías de identificación y demás datos de tu perfil.'
        },
        {
          category: 'cuenta',
          question: '¿Puedo cambiar mi residencia dentro de la plataforma?',
          answer: 'Sí, desde la opción "Cambiar Residencia" puedes solicitar la actualización cuando te mudes a otra vivienda dentro del fraccionamiento.'
        },
        // Tickets de Soporte
        {
          category: 'tickets',
          question: '¿Cómo puedo crear un ticket de soporte?',
          answer: 'Para crear un ticket, haz clic en la pestaña "Mis Tickets" y luego selecciona el botón "Nuevo Ticket". Completa el formulario con los detalles de tu solicitud y selecciona el área correspondiente.'
        },
        {
          category: 'tickets',
          question: '¿Cuál es el tiempo de respuesta promedio?',
          answer: 'Nos esforzamos por responder todos los tickets dentro de 24 a 48 horas hábiles. Los tickets marcados como urgentes reciben atención prioritaria.'
        },
        {
          category: 'tickets',
          question: '¿Puedo editar un ticket después de crearlo?',
          answer: 'Los tickets no pueden ser editados después de su creación. Si necesitas hacer cambios, puedes crear un nuevo ticket con la información actualizada o contactar al equipo de soporte.'
        },
        {
          category: 'tickets',
          question: '¿Qué significan los diferentes estados de un ticket?',
          answer: 'Los estados son: Nuevo (recién creado), En progreso (siendo atendido), Resuelto (solución proporcionada) y Cerrado (ticket finalizado).'
        },
        {
          category: 'tickets',
          question: '¿Cómo recibo notificaciones sobre mis tickets?',
          answer: 'Recibirás notificaciones por correo electrónico cuando tu ticket sea creado, cuando se realicen cambios en su estado, y cuando sea resuelto.'
        },
        {
          category: 'tickets',
          question: '¿Qué hacer si mi pregunta no está en las preguntas frecuentes?',
          answer: 'Si tu pregunta no está respondida aquí, puedes crear un ticket de soporte en la pestaña "Mis Tickets" y nuestro equipo te ayudará lo antes posible.'
        },
        // Visitas e Invitados
        {
          category: 'visitas',
          question: '¿Cómo genero una invitación para un visitante?',
          answer: 'Desde la sección de Invitaciones, captura los datos de tu visitante y genera un código QR o de acceso que podrás compartir con él para agilizar su ingreso.'
        },
        {
          category: 'visitas',
          question: '¿Cómo valida el vigilante el acceso de mis invitados?',
          answer: 'El personal de vigilancia escanea el código QR o ingresa el código de acceso en la caseta para registrar la entrada y salida del visitante.'
        },
        {
          category: 'visitas',
          question: '¿Puedo consultar el historial de mis visitas?',
          answer: 'Sí, en la sección de Histórico puedes revisar los accesos e invitaciones registrados anteriormente.'
        },
        // Paquetería
        {
          category: 'paqueteria',
          question: '¿Cómo me entero de que llegó un paquete?',
          answer: 'Recibirás una notificación cuando el vigilante registre la llegada de un paquete a tu nombre en la sección de Paquetería.'
        },
        {
          category: 'paqueteria',
          question: '¿Dónde recojo mis paquetes?',
          answer: 'Los paquetes se resguardan en la caseta de vigilancia hasta que los recojas; solo necesitas identificarte al retirarlos.'
        },
        // Servicio de Agua
        {
          category: 'agua',
          question: '¿Cómo reporto una falla en el servicio de agua?',
          answer: 'Desde la sección de Servicio de Agua puedes generar un reporte, o crear un ticket de soporte si necesitas seguimiento personalizado.'
        },
        {
          category: 'agua',
          question: '¿Dónde consulto el estatus de mi servicio de agua?',
          answer: 'En la sección de Servicio de Agua dentro de tu perfil puedes ver el estatus y los reportes relacionados con tu vivienda.'
        },
        // Estados de Cuenta
        {
          category: 'cuentas',
          question: '¿Dónde consulto mi estado de cuenta?',
          answer: 'En la sección de Estados de Cuenta puedes ver el detalle de tus cargos, pagos y saldo actual.'
        },
        {
          category: 'cuentas',
          question: '¿Con qué frecuencia se actualiza mi estado de cuenta?',
          answer: 'Se actualiza conforme la administración registra nuevos cargos y pagos, por lo que te recomendamos revisarlo periódicamente.'
        },
        // Mascotas
        {
          category: 'mascotas',
          question: '¿Cómo registro a mi mascota?',
          answer: 'En la sección de Mascotas puedes agregar el nombre, tipo y demás datos de tu mascota para mantener su registro actualizado ante la administración.'
        },
        // Casa Club
        {
          category: 'casa_club',
          question: '¿Cómo consulto información y amenidades de la Casa Club?',
          answer: 'En la sección de Casa Club puedes ver la información general, amenidades disponibles y solicitar accesos o invitaciones.'
        },
        {
          category: 'casa_club',
          question: '¿Puedo inscribirme a torneos de la Casa Club?',
          answer: 'Sí, revisa la sección de Torneos dentro de Casa Club para ver las convocatorias e inscribirte.'
        },
        // Multas de Tránsito
        {
          category: 'multas',
          question: '¿Cómo consulto si tengo una multa por exceso de velocidad?',
          answer: 'En la sección de Multas por Exceso de Velocidad podrás ver las infracciones registradas asociadas a tu vivienda.'
        },
        // Almacenamiento
        {
          category: 'almacenamiento',
          question: '¿Qué es el almacenamiento de la plataforma?',
          answer: 'Es el espacio destinado a guardar fotografías, documentos y evidencias generadas en la plataforma, como fotos de accesos, invitados y eventos.'
        },
        {
          category: 'almacenamiento',
          question: '¿Qué pasa si mi fraccionamiento se queda sin espacio de almacenamiento?',
          answer: 'El administrador puede solicitar almacenamiento adicional desde la tarjeta de Almacenamiento disponible en el inicio (Home).'
        },
        // Avisos y Directorio
        {
          category: 'avisos',
          question: '¿Dónde veo los avisos de la administración?',
          answer: 'En la sección de Avisos encontrarás comunicados, el Reglamento y el Aviso de Privacidad publicados por tu administrador.'
        },
        {
          category: 'avisos',
          question: '¿Cómo encuentro los datos de contacto de la administración o vecinos?',
          answer: 'Consulta el Directorio para ver la información de contacto disponible según tu rol dentro de la plataforma.'
        }
      ],
      tickets: [],
      areas: [],
      selectedTicket: null,
      dialogCreateTicket: false,
      dialogTicketDetails: false,
      loadingTickets: false,
      loadingCreateTicket: false,
      loadingAreas: false,
      errorAlert: '',
      errores: {},
      newTicket: {
        title: '',
        description: '',
        area_id: null,
        client_name: '',
        client_email: '',
        client_phone: ''
      },
      headers: []
    }
  },
  computed: {
    ...mapGetters(['getProfile']),
    filteredFaqs () {
      let result = this.faqs
      if (this.selectedCategory && this.selectedCategory !== 'todas') {
        result = result.filter(faq => faq.category === this.selectedCategory)
      }
      if (this.faqSearch) {
        const search = this.faqSearch.toLowerCase()
        result = result.filter(faq =>
          faq.question.toLowerCase().includes(search) ||
          faq.answer.toLowerCase().includes(search)
        )
      }
      return result
    }
  },
  watch: {
    getProfile: {
      handler (newProfile) {
        if (newProfile && newProfile.email) {
          this.loadAreas()
          this.loadTickets()
        }
      },
      immediate: true
    }
  },
  beforeMount () {
    this.headers = [
      {
        text: this.$t('id'),
        align: 'left',
        sortable: true,
        value: 'id'
      },
      {
        text: this.$t('titulo'),
        align: 'left',
        sortable: true,
        value: 'title'
      },
      {
        text: this.$t('cliente'),
        align: 'left',
        sortable: true,
        value: 'client_name'
      },
      {
        text: this.$t('area'),
        align: 'left',
        sortable: true,
        value: 'area'
      },
      {
        text: this.$t('estado'),
        align: 'center',
        sortable: true,
        value: 'status'
      },
      {
        text: this.$t('fecha_creacion'),
        align: 'left',
        sortable: true,
        value: 'created_date'
      },
      {
        text: this.$t('acciones'),
        align: 'center',
        sortable: false,
        value: 'actions'
      }
    ]
  },
  methods: {
    categoryLabel (value) {
      const cat = this.faqCategories.find(c => c.value === value)
      return cat ? cat.label : ''
    },

    categoryIcon (value) {
      const cat = this.faqCategories.find(c => c.value === value)
      return cat ? cat.icon : 'mdi-help'
    },

    async loadAreas () {
      this.loadingAreas = true
      try {
        const response = await ticketsService.getAreas()
        if (response.success) {
          this.areas = response.data
        }
      } catch (error) {
        console.error('Error loading areas:', error)
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al cargar las áreas')
      } finally {
        this.loadingAreas = false
      }
    },

    async loadTickets () {
      this.loadingTickets = true
      try {
        const response = await ticketsService.getTicketsByEmail(this.getProfile.email, 100)
        if (response.success) {
          this.tickets = response.data
        }
      } catch (error) {
        console.error('Error loading tickets:', error)
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al cargar los tickets')
      } finally {
        this.loadingTickets = false
      }
    },

    openCreateDialog () {
      this.resetForm()
      // Auto-llenar datos del usuario logueado
      if (this.getProfile) {
        this.newTicket.client_name = `${this.getProfile.first_name || ''} ${this.getProfile.last_name || ''}`.trim()
        this.newTicket.client_email = this.getProfile.email || ''
        this.newTicket.client_phone = this.getProfile.celular || this.getProfile.cellNumber || this.getProfile.phone || ''
      }
      this.errorAlert = ''
      this.dialogCreateTicket = true
    },

    async openDetailsDialog (ticket) {
      if (!ticket || !ticket.id) {
        console.error('Ticket inválido:', ticket)
        return
      }

      this.loadingTickets = true
      try {
        console.log('Cargando detalles del ticket:', ticket.id)
        const response = await ticketsService.getTicketById(ticket.id)
        console.log('Respuesta del API:', response)

        if (response && response.success && response.data) {
          this.selectedTicket = response.data
          this.dialogTicketDetails = true
          console.log('Ticket seleccionado:', this.selectedTicket)
        } else {
          throw new Error('Respuesta inválida del servidor')
        }
      } catch (error) {
        console.error('Error al obtener detalles:', error)
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_message', 'Error al cargar los detalles del ticket: ' + (error.message || error))
      } finally {
        this.loadingTickets = false
      }
    },

    async submitCreateTicket () {
      this.errores = {}
      this.errorAlert = ''

      if (!this.newTicket.title || !this.newTicket.title.trim()) {
        this.errores.title = ['El título es requerido']
        return
      }

      this.loadingCreateTicket = true
      try {
        const ticketData = {
          title: this.newTicket.title,
          description: this.newTicket.description || '',
          area_id: this.newTicket.area_id || null,
          client_name: this.newTicket.client_name || '',
          client_email: this.newTicket.client_email || '',
          client_phone: this.newTicket.client_phone || ''
        }

        const response = await ticketsService.createTicket(ticketData)

        if (response.success) {
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', 'Ticket creado exitosamente')
          this.$store.commit('snackbar_change_color', 'success')
          this.dialogCreateTicket = false
          this.resetForm()
          await this.loadTickets()
        }
      } catch (error) {
        console.error('Error creating ticket:', error)
        if (error.response && error.response.status === 400 && error.response.data) {
          const data = error.response.data
          if (data.error) {
            this.errorAlert = data.message || data.error
          } else {
            this.errores = data
          }
        } else {
          this.errorAlert = 'Error al crear el ticket. Por favor, intenta nuevamente.'
        }
      } finally {
        this.loadingCreateTicket = false
      }
    },

    resetForm () {
      this.newTicket = {
        title: '',
        description: '',
        area_id: null,
        client_name: '',
        client_email: '',
        client_phone: ''
      }
      this.errores = {}
      this.errorAlert = ''
    },

    getStatusColor (status) {
      const colorMap = {
        'Nuevo': '#FF9800',
        'En progreso': '#2196F3',
        'Resuelto': '#4CAF50',
        'Cerrado': '#9E9E9E'
      }
      return colorMap[status] || '#757575'
    },

    countByStatus (status) {
      return this.tickets.filter(ticket => ticket.status === status).length
    },

    formatDate (dateString) {
      if (!dateString) return '—'
      const date = new Date(dateString)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.v-data-table {
  cursor: pointer;
}

.v-data-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
}
</style>
